import { useState, useCallback, useRef } from 'react';
import { useRootSelector } from '../../../../../store/hooks';
import { selectLocationConfig } from '../../../../../store/features/locationConfig';
import { getPingIdentityAuthUrl } from '../../../../../helpers/authorization/getPingIdentityAuthUrl';
import { getPingIdentityProfileUrl } from '../../../../../helpers/authorization/getPingIdentityProfileUrl';
import { getPingIdentityLogoutUrl } from '../../../../../helpers/authorization/getPingIdentityLogoutUrl';
import { useOnClickOutside } from '../../../../../hooks/useOnClickOutside';
import Auth from '../../../../../services/AuthService';
import gtmService from '../../../../../utils/gtm';
import { usePingIdentityInfo } from '../usePingIdentityInfo';
import {
  SSO_LOGIN_PENDING_KEY,
  SSO_LOGIN_PENDING_VALUE,
  SSO_REDIRECT_DELAY_MS,
} from '../../constants';

export function useAccountMenuControls() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { origin, pathname } = useRootSelector(selectLocationConfig);
  const { userId } = usePingIdentityInfo();

  const handleCloseMenu = useCallback(() => {
    setIsDropdownOpen(false);
  }, []);

  useOnClickOutside(containerRef, handleCloseMenu);

  const handleAccountClick = useCallback(() => {
    setIsDropdownOpen((prev) => !prev);
  }, []);

  const handleLoginClick = useCallback(async () => {
    const authRedirectUrl = getPingIdentityAuthUrl({ origin, pathname });

    gtmService.ga4SSOLoginStart();
    sessionStorage.setItem(SSO_LOGIN_PENDING_KEY, SSO_LOGIN_PENDING_VALUE);

    await new Promise((resolve) => setTimeout(resolve, SSO_REDIRECT_DELAY_MS));

    window.location.assign(authRedirectUrl);
  }, [origin, pathname]);

  const handleLogoutClick = useCallback(async () => {
    gtmService.ga4SSOManualLogout(userId);

    Auth.removeTokenByCookieName('ping-token');
    Auth.removeTokenByCookieName('ping-refresh-token');

    const logoutUrl = getPingIdentityLogoutUrl({ origin, pathname });

    if (logoutUrl) {
      await new Promise((resolve) =>
        setTimeout(resolve, SSO_REDIRECT_DELAY_MS),
      );
      window.location.assign(logoutUrl);
    }
  }, [origin, pathname, userId]);

  const handleMyAccountClick = useCallback(() => {
    const profileUrl = getPingIdentityProfileUrl();

    if (profileUrl) {
      window.open(profileUrl, '_blank', 'noopener,noreferrer');
    }
  }, []);

  return {
    isDropdownOpen,
    containerRef,
    handleAccountClick,
    handleCloseMenu,
    handleLoginClick,
    handleLogoutClick,
    handleMyAccountClick,
  };
}
